/**
 * History Tab Component
 * Displays transaction history and activity timeline (Client Mode Only)
 * Uses data from transactions API endpoint
 */

import React, { useState } from 'react';
import {
  Calendar, Clock, FileText, TrendingUp, TrendingDown,
  RefreshCw, ChevronUp, ChevronDown, Download,
  // TASK-877: non-directional icons for the tiles that count rather than trend.
  ArrowLeftRight, Coins, Minus, Receipt, Wallet
} from 'lucide-react';
import { Transaction } from '../../services/api/portfolioApiService';

interface HistoryTabProps {
  clientId: string;
  transactions: Transaction[];
  onExport?: () => void;
  loading?: boolean;
}

interface Activity {
  id: string;
  type: 'transaction' | 'meeting' | 'rebalance' | 'document';
  date: string;
  title: string;
  description: string;
  amount?: number;
  status?: 'completed' | 'pending' | 'cancelled';
}

const HistoryTab: React.FC<HistoryTabProps> = ({
  clientId: _clientId,
  transactions = [],
  onExport,
  loading
}) => {
  const [sortField, setSortField] = useState<string>('trade_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('all');

  // Format currency
  const formatCurrency = (value: number, currency = 'CHF') => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format date
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filter transactions by date range
  const filterByDateRange = (tx: Transaction) => {
    if (dateRange === 'all') return true;
    
    const txDate = new Date(tx.trade_date);
    const now = new Date();
    
    switch (dateRange) {
      case '7d':
        return txDate >= new Date(now.setDate(now.getDate() - 7));
      case '30d':
        return txDate >= new Date(now.setDate(now.getDate() - 30));
      case '90d':
        return txDate >= new Date(now.setDate(now.getDate() - 90));
      case 'ytd':
        return txDate.getFullYear() === new Date().getFullYear();
      default:
        return true;
    }
  };

  // Filter transactions by type
  const filterByType = (tx: Transaction) => {
    if (filterType === 'all') return true;
    if (filterType === 'buy') return tx.transaction_type === 'BUY';
    if (filterType === 'sell') return tx.transaction_type === 'SELL';
    if (filterType === 'dividend') return tx.transaction_type === 'DIVIDEND';
    if (filterType === 'interest') return tx.transaction_type === 'INTEREST';
    if (filterType === 'fee') return tx.transaction_type === 'FEE';
    if (filterType === 'tax') return tx.transaction_type === 'TAX';
    if (filterType === 'transfer') return tx.transaction_type === 'TRANSFER_IN' || tx.transaction_type === 'TRANSFER_OUT';
    if (filterType === 'deposit') return tx.transaction_type === 'DEPOSIT';
    if (filterType === 'withdrawal') return tx.transaction_type === 'WITHDRAWAL';
    if (filterType === 'corporate') return tx.transaction_type === 'CORPORATE_ACTION';
    if (filterType === 'cancelled') return tx.is_cancelled === true;
    return true;
  };

  // TASK-877 (review): the direction has to agree with the number on screen.
  //
  // `totalRealizedPL` is a float sum over `realized_result_base_price`, so a book whose
  // gains and losses cancel lands on something like 2.3e-13 — which `formatCurrency`
  // renders as `CHF 0` (maximumFractionDigits: 0) while `> 0` calls it a gain. The tile
  // then shows a green up-arrow beside `CHF 0`, which is exactly the defect this ticket
  // reports. Anything that DISPLAYS as zero must be treated as zero, so the epsilon is
  // half the smallest displayed unit.
  const PL_EPSILON = 0.5;

  /** -1 loss, 0 flat, 1 gain — judged on the rounded figure the advisor actually sees. */
  const plDirection = (value: number): -1 | 0 | 1 =>
    Math.abs(value) < PL_EPSILON ? 0 : value > 0 ? 1 : -1;

  // Format realized P&L with sign
  const formatPL = (value: number | undefined | null, currency = 'CHF') => {
    if (value == null) return null;
    // `>= 0` put a `+` on zero, so a flat book read `+CHF 0` beside a neutral dash.
    const prefix = plDirection(value) > 0 ? '+' : '';
    return `${prefix}${formatCurrency(value, currency)}`;
  };

  // Apply filters and sorting
  const filteredTransactions = transactions
    .filter(filterByDateRange)
    .filter(filterByType);

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    const aValue = a[sortField as keyof Transaction];
    const bValue = b[sortField as keyof Transaction];
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    const aStr = String(aValue);
    const bStr = String(bValue);
    return sortDirection === 'asc' 
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });

  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Generate activity timeline (combining transactions with mock activities)
  const generateActivities = (): Activity[] => {
    const activities: Activity[] = [];
    
    // Add transactions to timeline
    transactions.forEach(tx => {
      activities.push({
        id: tx.id,
        type: 'transaction',
        date: tx.trade_date,
        title: `${tx.transaction_type} ${tx.security_name}`,
        description: `${tx.quantity} shares at ${formatCurrency(tx.price, tx.currency)}`,
        amount: tx.net_amount,
        status: new Date(tx.settlement_date) <= new Date() ? 'completed' : 'pending'
      });
    });
    
    // Add mock activities (in production, these would come from other endpoints)
    activities.push({
      id: 'meeting-1',
      type: 'meeting',
      date: '2024-12-01',
      title: 'Annual Portfolio Review',
      description: 'Discussed investment strategy and rebalancing needs',
      status: 'completed'
    });
    
    activities.push({
      id: 'rebalance-1',
      type: 'rebalance',
      date: '2024-11-15',
      title: 'Portfolio Rebalancing',
      description: 'Adjusted allocation to meet target weights',
      status: 'completed'
    });
    
    activities.push({
      id: 'doc-1',
      type: 'document',
      date: '2024-10-30',
      title: 'Q3 Performance Report',
      description: 'Quarterly performance review document sent',
      status: 'completed'
    });
    
    // Sort by date
    return activities.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  };

  const activities = generateActivities();
  const recentActivities = activities.slice(0, 5);

  // Calculate transaction summary
  const activeTx = transactions.filter(tx => !tx.is_cancelled);
  const transactionSummary = {
    totalBuys: activeTx.filter(tx => tx.transaction_type === 'BUY').length,
    totalSells: activeTx.filter(tx => tx.transaction_type === 'SELL').length,
    totalVolume: activeTx
      .filter(tx => tx.transaction_type === 'BUY' || tx.transaction_type === 'SELL')
      .reduce((sum, tx) => sum + Math.abs(tx.net_amount || 0), 0),
    totalFees: activeTx
      .filter(tx => tx.transaction_type === 'FEE' || tx.transaction_type === 'TAX')
      .reduce((sum, tx) => sum + Math.abs(tx.net_amount || 0), 0),
    totalRealizedPL: activeTx
      .reduce((sum, tx) => sum + (tx.realized_result_base_price || 0), 0),
    cancelledCount: transactions.filter(tx => tx.is_cancelled).length
  };

  // Get activity icon and color
  const getActivityInfo = (type: string) => {
    switch (type) {
      case 'transaction':
        return { icon: TrendingUp, color: 'text-blue-600 bg-blue-50' };
      case 'meeting':
        return { icon: Calendar, color: 'text-green-600 bg-green-50' };
      case 'rebalance':
        return { icon: RefreshCw, color: 'text-orange-600 bg-orange-50' };
      case 'document':
        return { icon: FileText, color: 'text-purple-600 bg-purple-50' };
      default:
        return { icon: Clock, color: 'text-gray-600 bg-gray-50' };
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards
          TASK-877: the first four tiles carried a HARDCODED `TrendingUp` or `TrendingDown`.
          They are counters — a transaction count, a buy/sell split, a volume, a fee total —
          and none of them has a direction to report. On an empty book the result was arrows
          pointing up on `0`, down on `0/0` and up on `CHF 0`, three contradictory verdicts
          on the same nothing. Replaced with icons that name the quantity instead of
          asserting a movement. Only Realized P/L is signed, and it now shows a flat dash at
          exactly zero rather than the green up-arrow `>= 0` used to give it. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Total Transactions</span>
            <Receipt className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-xl font-bold text-gray-900">
            {transactions.length}
            {transactionSummary.cancelledCount > 0 && (
              <span className="text-xs font-normal text-gray-400 ml-1">
                ({transactionSummary.cancelledCount} cancelled)
              </span>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Buy / Sell</span>
            <ArrowLeftRight className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-xl font-bold text-gray-900">
            {transactionSummary.totalBuys} / {transactionSummary.totalSells}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Total Volume</span>
            <Coins className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-xl font-bold text-gray-900">
            {formatCurrency(transactionSummary.totalVolume)}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Total Fees</span>
            <Wallet className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-xl font-bold text-gray-900">
            {formatCurrency(transactionSummary.totalFees)}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Realized P/L</span>
            {plDirection(transactionSummary.totalRealizedPL) === 0
              ? <Minus className="w-4 h-4 text-gray-400" />
              : plDirection(transactionSummary.totalRealizedPL) > 0
              ? <TrendingUp className="w-4 h-4 text-green-500" />
              : <TrendingDown className="w-4 h-4 text-red-500" />}
          </div>
          <div className={`text-xl font-bold ${
            plDirection(transactionSummary.totalRealizedPL) === 0
              ? 'text-gray-900'
              : plDirection(transactionSummary.totalRealizedPL) > 0
              ? 'text-green-600'
              : 'text-red-600'
          }`}>
            {formatPL(transactionSummary.totalRealizedPL)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Timeline */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-4">
              {recentActivities.map((activity) => {
                const activityInfo = getActivityInfo(activity.type);
                const Icon = activityInfo.icon;
                
                return (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${activityInfo.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {activity.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {activity.description}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {formatDate(activity.date)}
                      </div>
                    </div>
                    {activity.amount && (
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(activity.amount)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <button className="mt-4 text-sm text-blue-600 hover:text-blue-800">
              View all activities →
            </button>
          </div>
        </div>

        {/* Transaction History Table */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
              
              <div className="flex items-center gap-2">
                {/* Date Range Filter */}
                <select 
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="ytd">Year to Date</option>
                </select>
                
                {/* Type Filter */}
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                  <option value="dividend">Dividends</option>
                  <option value="interest">Interest</option>
                  <option value="fee">Fees</option>
                  <option value="tax">Tax</option>
                  <option value="transfer">Transfers</option>
                  <option value="deposit">Deposits</option>
                  <option value="withdrawal">Withdrawals</option>
                  <option value="corporate">Corporate Actions</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                
                {/* Export Button */}
                {onExport && (
                  <button
                    onClick={onExport}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('trade_date')}
                    >
                      <div className="flex items-center">
                        Date
                        {sortField === 'trade_date' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Security
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('net_amount')}
                    >
                      <div className="flex items-center justify-end">
                        Amount
                        {sortField === 'net_amount' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Realized P/L
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedTransactions.map((transaction) => {
                    const isSettled = new Date(transaction.settlement_date) <= new Date();
                    const isCancelled = transaction.is_cancelled === true;
                    const hasFxRate = transaction.fx_local_to_settlement_rate != null
                      && transaction.fx_local_to_settlement_rate !== 1;
                    const hasRealizedPL = transaction.realized_result_base_price != null
                      && transaction.realized_result_base_price !== 0;
                    const plValue = transaction.realized_result_base_price;

                    return (
                      <tr
                        key={transaction.id}
                        className={`hover:bg-gray-50 ${isCancelled ? 'opacity-60' : ''}`}
                      >
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${isCancelled ? 'line-through' : ''}`}>
                          {formatDate(transaction.trade_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            transaction.transaction_type === 'BUY'
                              ? 'bg-green-100 text-green-800'
                              : transaction.transaction_type === 'SELL'
                              ? 'bg-red-100 text-red-800'
                              : transaction.transaction_type === 'DIVIDEND' || transaction.transaction_type === 'INTEREST'
                              ? 'bg-blue-100 text-blue-800'
                              : transaction.transaction_type === 'FEE' || transaction.transaction_type === 'TAX'
                              ? 'bg-orange-100 text-orange-800'
                              : transaction.transaction_type === 'TRANSFER_IN' || transaction.transaction_type === 'DEPOSIT'
                              ? 'bg-teal-100 text-teal-800'
                              : transaction.transaction_type === 'TRANSFER_OUT' || transaction.transaction_type === 'WITHDRAWAL'
                              ? 'bg-amber-100 text-amber-800'
                              : transaction.transaction_type === 'CORPORATE_ACTION'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {transaction.transaction_type}
                          </span>
                          {transaction.description && (
                            <div className="text-xs text-gray-500 mt-1 max-w-[120px] truncate" title={transaction.description.split(' | ')[0]}>
                              {transaction.description.split(' | ')[0]}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-medium text-gray-900 ${isCancelled ? 'line-through' : ''}`}>
                            {transaction.security_name || transaction.description?.split(' | ')[0] || '-'}
                            {transaction.comment && (
                              <span className="ml-1 cursor-help" title={transaction.comment}>
                                <FileText className="w-3 h-3 inline text-gray-400" />
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {transaction.isin || transaction.description?.split(' | ').slice(1).join(' | ')}
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 ${isCancelled ? 'line-through' : ''}`}>
                          {transaction.quantity.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                          <div className={isCancelled ? 'line-through' : ''}>
                            {formatCurrency(transaction.price, transaction.price_currency || transaction.currency)}
                          </div>
                          {transaction.accrued_interest_local != null && transaction.accrued_interest_local !== 0 && (
                            <div className="text-xs text-blue-500 mt-0.5">
                              AI: {formatCurrency(transaction.accrued_interest_local, transaction.accrued_interest_local_ccy || transaction.currency)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                          <div className={isCancelled ? 'line-through' : ''}>
                            {formatCurrency(transaction.net_amount, transaction.currency)}
                          </div>
                          {hasFxRate && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              FX {transaction.fx_local_to_settlement_rate?.toFixed(4)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          {hasRealizedPL && !isCancelled ? (
                            <div>
                              <span className={plValue! >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                {formatPL(plValue, transaction.realized_result_base_price_ccy || 'CHF')}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-300">&mdash;</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {isCancelled ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Cancelled
                            </span>
                          ) : (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              isSettled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {isSettled ? 'Settled' : 'Pending'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {sortedTransactions.length === 0 && (
              <div className="px-6 py-12 text-center text-gray-500">
                No transactions found for the selected criteria
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryTab;